import React, { useEffect, useMemo, useState } from "react";
import { ClipboardList, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/use-auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import type {
  CarePlanV2,
  PatientV2,
  DeviceV2,
  NursingNoteV2,
  TreatmentPlanV2,
} from "../types";
import { DEVICE_TAB_CONFIG } from "../devices/deviceTabConfig";
import { useDevicesTabData } from "../devices/useDevicesTabData";
import { ActivitiesTab } from "./ActivitiesTab";
import { CarePlanTab } from "./CarePlanTab";
import { DevicesTab, type DeviceTabExportControls } from "./DevicesTab";
import { CallRecordingsTab } from "./CallRecordingsTab";
import { NursingNotesTab } from "./NursingNotesTab";
import { PatientSubTabPills } from "./PatientSubTabPills";
import { SupportTab } from "./SupportTab";
import "./patient-tabs.css";
import { useSearchParams } from "react-router";
import PatientHistoryTimeline from "@/components/patient/PatientHistoryTimeline";

const CONTENT_BG_CLASS = "bg-[#F3F4F6]";

const mainTabTriggerClass = cn(
  "patient-main-tab inline-flex h-11 min-w-0 flex-1 items-center justify-center rounded-none border-0 px-3 text-sm font-medium shadow-none outline-none",
  "data-[state=active]:shadow-none",
);

type Props = {
  patientId: string;
  patient: PatientV2 | null;
  carePlans: CarePlanV2[];
  treatmentPlans: TreatmentPlanV2[];
  devices: DeviceV2[];
  nursingNotes: NursingNoteV2[];
  activeTab?: string;
  onActiveTabChange?: (tab: string) => void;
  carePlanSubTab?: string;
  onCarePlanSubTabChange?: (tab: string) => void;
  deviceSubTab?: string;
  onDeviceSubTabChange?: (tab: string) => void;
  nursingNotesRefreshKey?: number;
  onOpenNursingNote?: (mode: "edit" | "view", note: NursingNoteV2) => void;
  onRefreshReadingCount?: () => void;
};

function PatientTabs({
  patientId,
  patient,
  carePlans,
  treatmentPlans,
  devices: _profileDevices,
  nursingNotes,
  activeTab: activeTabProp,
  onActiveTabChange,
  carePlanSubTab,
  onCarePlanSubTabChange,
  deviceSubTab: deviceSubTabProp,
  onDeviceSubTabChange,
  nursingNotesRefreshKey = 0,
  onOpenNursingNote,
  onRefreshReadingCount,
}: Props) {
  console.log("Patient Tab 2 -------------------------------------------------------------------------------");
  const { hasModuleAdminAccess, profile, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [internalTab, setInternalTab] = useState(searchParams.get("tab") || "basic-info");
  const [internalCarePlanSubTab, setInternalCarePlanSubTab] = useState("care-plan");
  const [internalDeviceSubTab, setInternalDeviceSubTab] = useState("device");
  const [deviceExportControls, setDeviceExportControls] =
    useState<DeviceTabExportControls | null>(null);
  const isPractitioner = user?.role?.toLowerCase() === "practitioner";
  const isCaretaker = user?.role === "caretaker";
  const isAdmin = hasModuleAdminAccess("canAccessPatients");
  const profileId = String((profile as { _id?: string } | null)?._id ?? "");
  const assignedCaretakerIdList = (patient?.assignedCaretakerIds ?? []).map((caretaker) =>
    typeof caretaker === "string" ? caretaker : caretaker?._id,
  );
  const isCaretakerAssignedToPatient =
    isCaretaker && !!profileId && assignedCaretakerIdList.includes(profileId);
  const showActivityTab = true || !isCaretaker || isCaretakerAssignedToPatient;
  const hasRPM = useMemo(
    () => (patient?.programs || []).join(",").includes("rpm"),
    [patient?.programs],
  );
  const hasTreatmentPlanData = treatmentPlans.length > 0;
  const showTxPlanTab = hasRPM || hasTreatmentPlanData;
  const activeTab = activeTabProp ?? internalTab;
  // console.log("--------------------------------");
  // console.log({activeTab});
  const setActiveTab = onActiveTabChange ?? setInternalTab;
  const resolvedCarePlanSubTab = carePlanSubTab ?? internalCarePlanSubTab;
  const setCarePlanSubTab = onCarePlanSubTabChange ?? setInternalCarePlanSubTab;
  const deviceSubTab = deviceSubTabProp ?? internalDeviceSubTab;
  const setDeviceSubTab = onDeviceSubTabChange ?? setInternalDeviceSubTab;

  const {
    devices: tabDevices,
    visibleTabs: deviceVisibleTabs,
    resolvedTimezone: devicesTimezone,
    hasDeviceData,
    loading: devicesTabLoading,
    ready: devicesTabReady,
    refreshDevices,
    resolveDefaultTab,
  } = useDevicesTabData({
    patientId,
    isActive: !!patientId,
    showOnlyAssignedDeviceTabs: patient?.typeOfPatient === "outpatient",
    isCaretaker,
  });
  const showDevicesTab = hasRPM || hasDeviceData;
  // console.log("showDevicesTab -->" , hasRPM, hasDeviceData)
  const isDevicesTabActive = activeTab === "devices" && showDevicesTab;

  const deviceSubTabItems = useMemo(() => {
    const tabs = devicesTabReady ? deviceVisibleTabs : DEVICE_TAB_CONFIG;
    return tabs.map((tab) => ({
      value: tab.value,
      label: tab.name,
      icon: tab.icon,
    }));
  }, [devicesTabReady, deviceVisibleTabs]);
  console.log("deviceSubTabItems -->" , deviceSubTabItems)
  console.log("deviceSubTab -->" , deviceSubTab)

  const carePlanSubTabItems = useMemo(
    () =>
      [
        { value: "care-plan", label: "Care Plan", icon: FileText },
        showTxPlanTab
          ? { value: "tx-plan", label: "Tx Plan", icon: ClipboardList }
          : null,
      ].filter(Boolean) as Array<{
        value: string;
        label: string;
        icon: typeof FileText;
      }>,
    [showTxPlanTab],
  );

  useEffect(() => {
    if (resolvedCarePlanSubTab === "tx-plan" && !showTxPlanTab) {
      setCarePlanSubTab("care-plan");
    }
  }, [resolvedCarePlanSubTab, showTxPlanTab, setCarePlanSubTab]);

  useEffect(() => {
    if (!isDevicesTabActive || !devicesTabReady) return;
    const resolved = resolveDefaultTab(deviceSubTab);
    if (resolved !== deviceSubTab) {
      setDeviceSubTab(resolved);
    }
  }, [
    isDevicesTabActive,
    devicesTabReady,
    deviceSubTab,
    resolveDefaultTab,
    setDeviceSubTab,
    deviceVisibleTabs,
  ]);

  useEffect(() => {
    if (
      patient &&
      devicesTabReady &&
      !showDevicesTab &&
      activeTab === "devices"
    ) {
      setActiveTab(showActivityTab ? "basic-info" : "nursing-notes");
    }
  }, [patient, devicesTabReady, showDevicesTab, showActivityTab, activeTab, setActiveTab]);

  useEffect(() => {
    if (!showActivityTab && activeTab === "basic-info") {
      setActiveTab("nursing-notes");
    }
  }, [showActivityTab, activeTab, setActiveTab]);


   // Only sync activeTab changes to URL (not vice versa)
   useEffect(() => {
    const currentTab = searchParams.get("tab");
    // Only update URL if activeTab is different from URL
    if (currentTab !== activeTab) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("tab", activeTab);
          return newParams;
        },
        { replace: false },
      ); // Use push instead of replace to support back button
    }
  }, [activeTab]); // Remove setSearchParams and searchParams from dependencies

  // Listen to URL changes (like browser back/forward)
  useEffect(() => {
    const currentTab = searchParams.get("tab");
    // Only update state if URL tab is different and valid
    if (currentTab && currentTab !== activeTab) {
      setActiveTab(currentTab);
    }
  }, [searchParams.get("tab")]); // Only depend on the tab value, not entire searchParams


  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
      className={cn("teachers flex h-full min-h-0 w-full min-w-0 flex-col gap-0", CONTENT_BG_CLASS)}
    >
      <div className="z-20 shrink-0 overflow-visible bg-white">
        <div className="patient-tab-bar flex h-11 items-stretch overflow-visible border-b border-[#D1D5DB]">
          <TabsList className="mb-0 flex h-11 w-full min-w-0 overflow-visible rounded-none border-0 bg-transparent p-0">
            {showActivityTab ? (
              <TabsTrigger value="basic-info" className={mainTabTriggerClass}>
                Activities
              </TabsTrigger>
            ) : null}
            <TabsTrigger value="nursing-notes" className={mainTabTriggerClass}>
              Nursing Notes
            </TabsTrigger>
            <TabsTrigger value="care-plan" className={mainTabTriggerClass}>
              Care Plan
            </TabsTrigger>
            {showDevicesTab ? (
              <TabsTrigger value="devices" className={mainTabTriggerClass}>
                Devices
              </TabsTrigger>
            ) : null}
            {(isAdmin || isCaretaker) ? (
              <TabsTrigger value="calls" className={mainTabTriggerClass}>
                Calls
              </TabsTrigger>
            ) : null}
            {(isAdmin || isCaretaker) ? (
              <TabsTrigger value="support" className={mainTabTriggerClass}>
                Support
              </TabsTrigger>
            ) : null}
          </TabsList>
        </div>

        {activeTab === "care-plan" ? (
          <PatientSubTabPills
            tabs={carePlanSubTabItems}
            activeTab={resolvedCarePlanSubTab}
            onTabChange={setCarePlanSubTab}
            ariaLabel="Care plan sections"
          />
        ) : null}
        {activeTab === "devices" && showDevicesTab ? (
          <PatientSubTabPills
            tabs={deviceSubTabItems}
            activeTab={deviceSubTab}
            onTabChange={setDeviceSubTab}
            ariaLabel="Device sections"
            labelOnlyWhenActive
            trailing={
              deviceExportControls?.visible ? (
                <Button
                  type="button"
                  variant="outline"
                  className="shrink-0 bg-white"
                  disabled={deviceExportControls.loading || !patient?._id}
                  onClick={deviceExportControls.onExport}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {deviceExportControls.loading ? "Exporting…" : "XLSX"}
                </Button>
              ) : null
            }
          />
        ) : null}
      </div>

      <div className={cn("thin-invisible-scrollbar min-h-0 flex-1 overflow-auto", CONTENT_BG_CLASS)}>
        {showActivityTab ? (
          <TabsContent value="basic-info" className="mt-0 min-w-0 p-3">
            <div className="min-w-0 max-w-full space-y-6 overflow-x-auto">
              {/* <div>
              <ActivitiesTab />
            </div> */}
              <div className="rounded-lg border border-[#E5E7EB] bg-white p-4 shadow-sm">
                <PatientHistoryTimeline patientId={patientId} enabled={activeTab === "basic-info"} />
              </div>
            </div>
          </TabsContent>
        ) : null}
        <TabsContent value="care-plan" className="mt-0 min-w-0 p-3">
          <div className="min-w-0 max-w-full overflow-x-auto">
            <CarePlanTab
              patient={patient}
              patientId={patientId}
              carePlans={carePlans}
              treatmentPlans={treatmentPlans}
              activeSubTab={resolvedCarePlanSubTab}
            />
          </div>
        </TabsContent>
        {showDevicesTab ? (
          <TabsContent value="devices" className="mt-0 min-w-0 space-y-6 p-3 pt-0">
            <div className="min-w-0 max-w-full overflow-x-auto">
              {isDevicesTabActive ? (
                <DevicesTab
                  patient={patient}
                  devices={tabDevices}
                  devicesTimezone={devicesTimezone}
                  visibleTabs={deviceVisibleTabs}
                  devicesLoading={devicesTabLoading}
                  devicesReady={devicesTabReady}
                  isPractitioner={isPractitioner}
                  activeSubTab={deviceSubTab}
                  onActiveSubTabChange={setDeviceSubTab}
                  onExportControlsChange={setDeviceExportControls}
                  onRefreshDevices={refreshDevices}
                  onRefreshReadingCount={onRefreshReadingCount}
                />
              ) : null}
            </div>
          </TabsContent>
        ) : null}
        {(isAdmin || isCaretaker) ? (
        <TabsContent value="calls" className="mt-0 min-w-0 p-3">
          <div className="min-w-0 max-w-full overflow-x-auto">
              <CallRecordingsTab patientId={patientId} enabled={activeTab === "calls"} />
            </div>
          </TabsContent>
        ) : null}
        <TabsContent value="nursing-notes" className="mt-0 min-w-0 p-3">
          <div className="min-w-0 max-w-full overflow-x-auto">
            <NursingNotesTab
              patientId={patientId}
              patient={patient}
              notes={nursingNotes}
              listRefreshKey={nursingNotesRefreshKey}
              onOpenNursingNote={onOpenNursingNote}
            />
          </div>
        </TabsContent>
        {(isAdmin || isCaretaker) ? (
        <TabsContent value="support" className="mt-0 min-w-0 p-3">
          <div className="min-w-0 max-w-full overflow-x-auto">
            <SupportTab patientId={patientId} patient={patient} />
          </div>
        </TabsContent>
        ) : null}

      </div>
    </Tabs>
  );
}

export const PatientTabsMemo = React.memo(PatientTabs);
